/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import StaffTimeOff from '../components/StaffTimeOff';
import { toET, todayET } from '../utils/et';
import { format, isToday, parseISO, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay, addDays } from 'date-fns';

const REQUEST_TYPES = {
  time_off:            { label: 'Time off',          bg: '#E6F1FB', color: '#185FA5' },
  availability_change: { label: 'Availability change', bg: '#EEEDFE', color: '#534AB7' },
  call_in:             { label: 'Call in',            bg: '#FCEBEB', color: '#A32D2D' },
  shift_swap:          { label: 'Shift swap',         bg: '#FAEEDA', color: '#854F0B' },
  early_release:       { label: 'Early release',     bg: '#FAEEDA', color: '#854F0B' },
  note_to_admin:       { label: 'Note',               bg: '#f0f0f0', color: '#666' },
  keep_wc_van:         { label: 'Keep WC van',       bg: '#FFF7E6', color: '#854F0B' },
  hours_correction:    { label: 'Hours correction',  bg: '#FCEBEB', color: '#A32D2D' },
};

const WORK_TYPES = {
  vehicle_maintenance: 'Vehicle maintenance',
  vehicle_cleaning:    'Vehicle cleaning',
  training:            'Training',
  office_admin:        'Office / admin',
  other:               'Other',
};

const STATUS_COLORS = {
  pending:  { bg: '#FAEEDA', color: '#854F0B' },
  approved: { bg: '#E1F5EE', color: '#0F6E56' },
  denied:   { bg: '#FCEBEB', color: '#A32D2D' },
};

function Badge({ bg, color, label }) {
  return (
    <span style={{ background: bg, color, fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</span>
  );
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function RequestCard({ request, onApprove, onDeny, onEdit, onDelete, drivers }) {
  const type = REQUEST_TYPES[request.request_type] || REQUEST_TYPES.note_to_admin;
  const status = STATUS_COLORS[request.status] || STATUS_COLORS.pending;
  const isUrgent = request.request_type === 'call_in' && request.start_date && isToday(parseISO(request.start_date));
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    startDate: request.start_date ? String(request.start_date).substring(0, 10) : '',
    endDate: request.end_date ? String(request.end_date).substring(0, 10) : '',
    startTime: request.start_time || '',
    endTime: request.end_time || '',
    message: request.message || '',
    coverageDriverId: request.coverage_driver_id || '',
  });
  const [saving, setSaving] = useState(false);

  const isOvernightCoverage = request.is_overnight_coverage;

  return (
    <div style={{
      border: `0.5px solid ${isOvernightCoverage ? '#F09595' : isUrgent ? '#FAC775' : '#e0e0db'}`,
      borderRadius: 8, padding: '12px 14px', marginBottom: 8,
      background: isUrgent ? '#FAEEDA22' : 'white',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {isUrgent && <Badge bg="#FCEBEB" color="#A32D2D" label="URGENT — today" />}
            <Badge {...type} />
            <span style={{ fontSize: 13, fontWeight: 500 }}>{request.driver_name || 'Driver'}</span>
            {request.direct && <Badge bg="#F1EFE8" color="#5F5E5A" label="No request: on schedule" />}
          </div>
          {request.start_date && (() => {
            const startStr = String(request.start_date).substring(0, 10);
            const endStr = request.end_date ? String(request.end_date).substring(0, 10) : null;
            const startParts = startStr.split('-');
            const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
            const startLabel = format(start, 'EEE, MMM d');
            let dateDisplay = startLabel;
            if (endStr && endStr !== startStr) {
              const endParts = endStr.split('-');
              const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
              const endLabel = format(end, 'EEE, MMM d');
              const diffDays = Math.round((end - start) / 86400000) + 1;
              dateDisplay = `${startLabel} → ${endLabel} (${diffDays} days)`;
            }
            const today = toET(new Date());
            const isUpcoming = start > today;
            const isPast = start < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const timeDisplay = request.start_time
              ? `${formatTime(request.start_time)}${request.end_time ? ' – ' + formatTime(request.end_time) : ''}`
              : '';
            return (
              <div style={{ fontSize: 13, color: isPast ? '#999' : '#333', marginBottom: 4, fontWeight: 500 }}>
                {dateDisplay}{timeDisplay ? ` · ${timeDisplay}` : ''}
              </div>
            );
          })()}
          {isOvernightCoverage && (
            <div style={{ background: '#FCEBEB', border: '0.5px solid #F09595', borderRadius: 6, padding: '6px 10px', marginBottom: 6, fontSize: 12 }}>
              <strong style={{ color: '#A32D2D' }}>OVERNIGHT COVERAGE NEEDED</strong>
              <span style={{ color: '#666', marginLeft: 6 }}>
                {request.coverage_driver_id
                  ? (request.coverage_confirmed ? 'Covered — replacement confirmed' : 'Replacement assigned — awaiting confirmation')
                  : 'No replacement assigned yet'}
              </span>
            </div>
          )}
          {editing && (
            <div style={{ background: '#f9f9f8', padding: '10px', borderRadius: 8, marginBottom: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                <div><label style={{ fontSize: 10, color: '#999' }}>Start date</label><input type="date" value={editForm.startDate} onChange={e => setEditForm(f => ({...f, startDate: e.target.value}))} style={{ width: '100%', fontSize: 11 }} /></div>
                <div><label style={{ fontSize: 10, color: '#999' }}>End date</label><input type="date" value={editForm.endDate} onChange={e => setEditForm(f => ({...f, endDate: e.target.value}))} style={{ width: '100%', fontSize: 11 }} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                <div><label style={{ fontSize: 10, color: '#999' }}>Start time</label><input type="time" value={editForm.startTime} onChange={e => setEditForm(f => ({...f, startTime: e.target.value}))} style={{ width: '100%', fontSize: 11 }} /></div>
                <div><label style={{ fontSize: 10, color: '#999' }}>End time</label><input type="time" value={editForm.endTime} onChange={e => setEditForm(f => ({...f, endTime: e.target.value}))} style={{ width: '100%', fontSize: 11 }} /></div>
              </div>
              <div style={{ marginBottom: 6 }}><label style={{ fontSize: 10, color: '#999' }}>Notes</label><input value={editForm.message} onChange={e => setEditForm(f => ({...f, message: e.target.value}))} style={{ width: '100%', fontSize: 11 }} /></div>
              {isOvernightCoverage && drivers && (
                <div style={{ marginBottom: 6 }}>
                  <label style={{ fontSize: 10, color: '#A32D2D', fontWeight: 500 }}>Overnight replacement driver</label>
                  <select value={editForm.coverageDriverId} onChange={e => setEditForm(f => ({...f, coverageDriverId: e.target.value}))} style={{ width: '100%', fontSize: 11 }}>
                    <option value="">Select replacement...</option>
                    {drivers.filter(d => d.id !== request.driver_id).map(d => (
                      <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setEditing(false)} style={{ fontSize: 10, padding: '3px 8px' }}>Cancel</button>
                <button disabled={saving} onClick={async () => {
                  setSaving(true);
                  try {
                    await onEdit(request.id, editForm);
                    setEditing(false);
                  } catch (e) { alert('Save failed'); }
                  finally { setSaving(false); }
                }} style={{ fontSize: 10, padding: '3px 8px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 4 }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
          {request.request_type === 'keep_wc_van' && (
            <div style={{ fontSize: 12, color: '#444', marginBottom: 4, background: '#FFF7E6', padding: '6px 8px', borderRadius: 6, border: '0.5px solid #F2D5A3' }}>
              Wants to keep <strong>{request.current_vehicle_name || 'WC van'}</strong>
              {' '}for sedan trip{request.transport_number ? ` ${request.transport_number}` : ''}
              {request.transport_scheduled_time ? ` at ${format(toET(request.transport_scheduled_time), 'h:mm a')}` : ''}
              {request.transport_destination_address ? ` to ${request.transport_destination_address}` : ''}
            </div>
          )}
          {request.message && request.request_type !== 'keep_wc_van' && (
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>"{request.message}"</div>
          )}
          {request.request_type === 'hours_correction' && (() => {
            const pl = typeof request.payload === 'string' ? (() => { try { return JSON.parse(request.payload); } catch (e) { return null; } })() : request.payload;
            const prop = pl && pl.proposed;
            const t = (v) => v ? format(toET(v), 'h:mm a') : '--';
            return (
              <div style={{ fontSize: 12, color: '#444', marginBottom: 4, background: '#FCEBEB', padding: '6px 8px', borderRadius: 6, border: '0.5px solid #E0A6A6' }}>
                <div><strong>On the trip now:</strong> en route {t(request.transport_en_route_time)}, complete {t(request.transport_completed_time)}
                  {request.driver_hours != null ? ` · paid ${Number(request.driver_hours).toFixed(2)} h / $${Number(request.driver_pay || 0).toFixed(2)}` : ''}
                  {request.billing_hours != null ? ` · billed ${Number(request.billing_hours).toFixed(2)} h / $${Number(request.billing_total || 0).toFixed(2)}` : ''}
                </div>
                {prop ? (
                  <div style={{ marginTop: 2 }}><strong>Driver says it should be:</strong>
                    {prop.enRouteTime ? ` en route ${t(prop.enRouteTime)}` : ''}{prop.completedTime ? ` · complete ${t(prop.completedTime)}` : ''}
                    <span style={{ color: '#666' }}> (Accept applies these and refigures pay and billing)</span>
                  </div>
                ) : (
                  <div style={{ marginTop: 2, color: '#666' }}>No times proposed; Accept just records the outcome.</div>
                )}
              </div>
            );
          })()}
          <div style={{ fontSize: 11, color: '#aaa' }}>
            {request.direct ? "Entered" : "Submitted"} {request.created_at ? format(toET(request.created_at), "MMM d, h:mm a") : ""}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <Badge {...status} label={request.status} />
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            {!request.direct && <button onClick={() => setEditing(!editing)} style={{ fontSize: 10, padding: '3px 8px', color: '#999', background: 'none', border: '0.5px solid #e0e0db', borderRadius: 4 }}>Edit</button>}
            <button onClick={() => onDelete(request.id)} style={{ fontSize: 10, padding: '3px 8px', color: '#A32D2D', background: 'none', border: '0.5px solid #F09595', borderRadius: 4 }}>Delete</button>
          </div>
          {request.status === 'pending' && (
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button onClick={() => onDeny(request.id, request)} style={{ fontSize: 11, padding: '4px 10px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595' }}>Deny</button>
              <button onClick={() => onApprove(request.id, request)} style={{ fontSize: 11, padding: '4px 10px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>Approve</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WorkLogCard({ entry, onApprove, onDeny }) {
  const status = STATUS_COLORS[entry.status] || STATUS_COLORS.pending;
  const workType = WORK_TYPES[entry.work_type] || entry.work_type;
  const dateStr = entry.work_date ? String(entry.work_date).substring(0, 10) : null;
  const dateParts = dateStr ? dateStr.split('-') : null;
  const dateLabel = dateParts
    ? format(new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])), 'EEE MMM d')
    : '--';

  return (
    <div style={{ border: '0.5px solid #e0e0db', borderRadius: 8, padding: '12px 14px', marginBottom: 8, background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            <Badge bg="#EEEDFE" color="#3C3489" label="Work log" />
            <span style={{ fontSize: 13, fontWeight: 500 }}>{entry.driver_name || 'Driver'}</span>
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
            {workType} · {dateLabel}
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
            {formatTime(entry.start_time)} – {formatTime(entry.end_time)}
            {entry.hours && ` · ${parseFloat(entry.hours).toFixed(2)}h`}
          </div>
          {entry.vehicle_note && (
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>🚐 {entry.vehicle_note}</div>
          )}
          {entry.notes && (
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>"{entry.notes}"</div>
          )}
          <div style={{ fontSize: 11, color: '#aaa' }}>
            Submitted {entry.created_at ? format(toET(entry.created_at), 'MMM d, h:mm a') : ''}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <Badge {...status} label={entry.status} />
          {entry.status === 'pending' && (
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button onClick={() => onDeny(entry.id)} style={{ fontSize: 11, padding: '4px 10px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595' }}>Deny</button>
              <button onClick={() => onApprove(entry.id)} style={{ fontSize: 11, padding: '4px 10px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>Approve</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Requests() {
  const [requests, setRequests] = useState([]);
  const [workLog, setWorkLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [activeTab, setActiveTab] = useState('requests');
  const [showAddRequest, setShowAddRequest] = useState(false);

  const fetchAll = async () => {
    try {
      const [rRes, wRes] = await Promise.all([
        api.get('/drivers/requests'),
        api.get('/drivers/work-log/pending'),
      ]);
      // Direct time off (no request) rides along on the Approved list (Paula 2026-10-20)
      setRequests([...(rRes.data.requests || []), ...(rRes.data.directTimeOff || [])]);
      setWorkLog(wRes.data.entries || []);
    } catch (err) {
      setRequests([]);
      setWorkLog([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const [driversList, setDriversList] = useState([]);
  useEffect(() => {
    api.get('/drivers')
      .then(r => setDriversList(r.data.drivers || []))
      .catch(() => {});
  }, []);

  const handleEditRequest = async (id, form) => {
    await api.put(`/drivers/requests/${id}`, {
      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
      startTime: form.startTime || undefined,
      endTime: form.endTime || undefined,
      message: form.message || undefined,
      coverageDriverId: form.coverageDriverId || undefined,
    });
    fetchAll();
  };

  // B-103: an hours_correction decision must record what actually happened —
  // the backend rejects the decision without a resolution.
  const askResolution = (decision) => {
    const c = window.prompt(
      `Outcome for this hours correction (${decision})?\n` +
      '1 = corrected (pay/billing was fixed)\n' +
      '2 = no change needed (numbers were already right)\n' +
      '3 = handed off (someone else will fix it)\n' +
      'Enter 1, 2, or 3:'
    );
    return { 1: 'corrected', 2: 'no_change_needed', 3: 'handed_off' }[String(c || '').trim()] || null;
  };

  const hasProposed = (request) => {
    if (!request || request.request_type !== 'hours_correction') return false;
    const pl = typeof request.payload === 'string' ? (() => { try { return JSON.parse(request.payload); } catch (e) { return null; } })() : request.payload;
    return !!(pl && pl.proposed && (pl.proposed.enRouteTime || pl.proposed.completedTime));
  };

  // Meal voucher requests (Paula 2026-10-20): Accept adds the meal.
  const mealAsked = (request) => {
    if (!request || request.request_type !== 'hours_correction') return null;
    const pl = typeof request.payload === 'string' ? (() => { try { return JSON.parse(request.payload); } catch (e) { return null; } })() : request.payload;
    if (pl && pl.proposed && Number(pl.proposed.mealAmount) > 0) return Number(pl.proposed.mealAmount);
    const msg = String(request.message || '');
    if (!/\b(meal|voucher|food|lunch|dinner|breakfast)\b/i.test(msg)) return null;
    const m = msg.match(/\$\s*(\d+(?:\.\d{1,2})?)/);
    return m ? Number(m[1]) : 20;
  };

  const handleApprove = async (id, request) => {
    try {
      const body = { status: 'approved' };
      if (request && request.request_type === 'hours_correction') {
        const meal = mealAsked(request);
        if (meal != null) {
          const amt = window.prompt('Meal amount to add for this driver (max $20 per transporter):', String(Math.min(20, meal)));
          if (amt == null) return;
          const n = Number(String(amt).replace(/[$\s]/g, ''));
          if (!(n > 0)) { alert('Enter a dollar amount, e.g. 20 or 14.50'); return; }
          body.mealAmount = n;
        }
        // Proposed times or a meal on the request: Accept = apply them (Paula 2026-10-13, 10-20)
        const resolution = (hasProposed(request) || meal != null) ? 'corrected' : askResolution('approve');
        if (!resolution) return;
        body.resolution = resolution;
      }
      const r = await api.put(`/drivers/requests/${id}`, body);
      if (r.data && r.data.mealNote) alert(r.data.mealNote);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not approve request');
    }
  };

  const handleDeny = async (id, request) => {
    try {
      const body = { status: 'denied' };
      if (request && request.request_type === 'hours_correction') {
        const resolution = hasProposed(request) ? 'no_change_needed' : askResolution('deny');
        if (!resolution) return;
        body.resolution = resolution;
        const reply = window.prompt('Reply to the driver (optional):', '');
        if (reply && reply.trim()) body.adminResponse = reply.trim();
      }
      await api.put(`/drivers/requests/${id}`, body);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not deny request');
    }
  };

  const handleDelete = async (id) => {
    const direct = requests.find(r => r.id === id && r.direct);
    if (direct) {
      if (!window.confirm(`Remove this time off from ${direct.driver_name}'s schedule?`)) return;
      try {
        await api.delete('/drivers/direct-time-off', { data: { exceptionIds: direct.exception_ids } });
        fetchAll();
      } catch (err) {
        alert(err.response?.data?.error || 'Could not remove time off');
      }
      return;
    }
    if (!window.confirm('Delete this request and remove any associated schedule exceptions?')) return;
    try {
      await api.delete(`/drivers/requests/${id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete request');
    }
  };

  const handleApproveWork = async (id) => {
    try {
      await api.put(`/drivers/work-log/${id}`, { status: 'approved' });
      fetchAll();
    } catch (err) { alert('Could not approve work log entry'); }
  };

  const handleDenyWork = async (id) => {
    try {
      await api.put(`/drivers/work-log/${id}`, { status: 'denied' });
      fetchAll();
    } catch (err) { alert('Could not deny work log entry'); }
  };

  const urgent = requests.filter(r => {
    try {
      return r.request_type === 'call_in' && r.start_date && isToday(parseISO(String(r.start_date).slice(0, 10))) && r.status === 'pending';
    } catch { return false; }
  });

  const wcRequests = requests.filter(r => r.request_type === 'keep_wc_van');
  const otherRequests = requests.filter(r => r.request_type !== 'keep_wc_van');
  const pendingCount = otherRequests.filter(r => r.status === 'pending').length;
  const pendingWcCount = wcRequests.filter(r => r.status === 'pending').length;
  const pendingWorkCount = workLog.filter(r => r.status === 'pending').length;

  // Sort: upcoming/current requests at the top (soonest first), then the ones
  // whose date has already passed fall to the bottom in chronological order
  // (oldest first). Requests with no date sort to the very end.
  // 2026-08-19 (Geraldo's vacation week): a multi-day range that STARTED in
  // the past but is still running counts as CURRENT — judge by end_date
  // (falling back to start_date), or mid-week time off sinks below stale
  // history and looks missing from the approved list.
  const todayStr = todayET();
  const byUpcomingThenPast = (a, b) => {
    const sa = a.start_date ? String(a.start_date) : null;
    const sb = b.start_date ? String(b.start_date) : null;
    if (!sa && !sb) return 0;
    if (!sa) return 1;
    if (!sb) return -1;
    const endA = String(a.end_date || a.start_date).slice(0, 10);
    const endB = String(b.end_date || b.start_date).slice(0, 10);
    const aUpcoming = endA >= todayStr;
    const bUpcoming = endB >= todayStr;
    if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  };

  const filteredBase = activeTab === 'wc_van' ? wcRequests : otherRequests;
  const filtered = filteredBase.filter(r => {
    if (filter === 'pending') return r.status === 'pending';
    if (filter === 'approved') return r.status === 'approved';
    if (filter === 'denied') return r.status === 'denied';
    return true;
  }).sort(byUpcomingThenPast);

  const filteredWork = workLog.filter(r => {
    if (filter === 'pending') return r.status === 'pending';
    if (filter === 'approved') return r.status === 'approved';
    if (filter === 'denied') return r.status === 'denied';
    return true;
  });
  const totalPending = pendingCount + pendingWcCount + pendingWorkCount;

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>
            Driver requests
            {totalPending > 0 && (
              <span style={{ display: 'inline-block', background: '#E24B4A', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 10, textAlign: 'center', lineHeight: '18px', marginLeft: 8, fontWeight: 500 }}>{totalPending}</span>
            )}
          </h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Time off, call-ins, vehicle requests, work log</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={fetchAll} style={{ fontSize: 12 }}>Refresh</button>
          <button onClick={() => setShowAddRequest(true)} style={{ fontSize: 12, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '4px 12px' }}>+ Add request</button>
        </div>
      </div>

      {JSON.parse(localStorage.getItem('nemt_user') || '{}').role === 'admin' && <StaffTimeOff mode="all" />}

      {urgent.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#A32D2D', marginBottom: 8 }}>Urgent — needs attention now</div>
          {urgent.map(r => <RequestCard key={r.id} request={r} onApprove={handleApprove} onDeny={handleDeny} onDelete={handleDelete} onEdit={handleEditRequest} drivers={driversList} />)}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button onClick={() => setActiveTab('requests')} style={{ fontSize: 12, padding: '5px 14px', background: activeTab === 'requests' ? '#185FA5' : 'white', color: activeTab === 'requests' ? 'white' : '#666', border: `0.5px solid ${activeTab === 'requests' ? '#185FA5' : '#e0e0db'}` }}>
          Requests {pendingCount > 0 ? `(${pendingCount})` : ''}
        </button>
        <button onClick={() => setActiveTab('wc_van')} style={{ fontSize: 12, padding: '5px 14px', background: activeTab === 'wc_van' ? '#185FA5' : 'white', color: activeTab === 'wc_van' ? 'white' : '#666', border: `0.5px solid ${activeTab === 'wc_van' ? '#185FA5' : '#e0e0db'}` }}>
          WC van {pendingWcCount > 0 ? `(${pendingWcCount})` : ''}
        </button>
        <button onClick={() => setActiveTab('worklog')} style={{ fontSize: 12, padding: '5px 14px', background: activeTab === 'worklog' ? '#185FA5' : 'white', color: activeTab === 'worklog' ? 'white' : '#666', border: `0.5px solid ${activeTab === 'worklog' ? '#185FA5' : '#e0e0db'}` }}>
          Work log {pendingWorkCount > 0 ? `(${pendingWorkCount})` : ''}
        </button>
        <button onClick={() => setActiveTab('calendar')} style={{ fontSize: 12, padding: '5px 14px', background: activeTab === 'calendar' ? '#185FA5' : 'white', color: activeTab === 'calendar' ? 'white' : '#666', border: `0.5px solid ${activeTab === 'calendar' ? '#185FA5' : '#e0e0db'}` }}>
          Team Calendar
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { key: 'pending', label: `Pending` },
          { key: 'approved', label: 'Approved' },
          { key: 'denied', label: 'Denied' },
          { key: 'all', label: 'All' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ fontSize: 12, padding: '4px 12px', background: filter === f.key ? '#185FA5' : 'white', color: filter === f.key ? 'white' : '#666', border: `0.5px solid ${filter === f.key ? '#185FA5' : '#e0e0db'}` }}>
            {f.label}
          </button>
        ))}
      </div>

      {activeTab === 'calendar' ? (
        <TeamCalendar drivers={driversList} />
      ) : loading ? (
        <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>
      ) : (activeTab === 'requests' || activeTab === 'wc_van') ? (
        filtered.length === 0 ? (
          <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#999', fontSize: 13 }}>
            {filter === 'pending' ? 'No pending requests' : 'No requests found'}
          </div>
        ) : (
          filtered.map(r => <RequestCard key={r.id} request={r} onApprove={handleApprove} onDeny={handleDeny} onDelete={handleDelete} onEdit={handleEditRequest} drivers={driversList} />)
        )
      ) : (
        filteredWork.length === 0 ? (
          <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#999', fontSize: 13 }}>
            {filter === 'pending' ? 'No pending work log entries' : 'No work log entries found'}
          </div>
        ) : (
          filteredWork.map(e => <WorkLogCard key={e.id} entry={e} onApprove={handleApproveWork} onDeny={handleDenyWork} />)
        )
      )}

      {showAddRequest && (
        <AddRequestModal
          drivers={driversList}
          onClose={() => setShowAddRequest(false)}
          onSaved={() => { setShowAddRequest(false); fetchAll(); }}
        />
      )}
    </div>
  );
}

function TeamCalendar() {
  const [weekStart, setWeekStart] = useState(startOfWeek(toET(new Date()), { weekStartsOn: 1 }));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    setLoading(true);
    const start = format(weekStart, 'yyyy-MM-dd');
    const end = format(weekEnd, 'yyyy-MM-dd');
    api.get(`/drivers/team-availability?startDate=${start}&endDate=${end}`)
      .then(r => setData(r.data.drivers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [weekStart]);

  const getStatus = (driver, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const exc = (driver.exceptions || []).find(e => String(e.exception_date).substring(0, 10) === dateStr);
    if (exc) {
      if (['day_off', 'call_in', 'unavailable', 'leave', 'training'].includes(exc.exception_type)) return 'off';
      if (exc.exception_type === 'available_extra') return 'on_call';
    }
    // Check weekly pattern
    const wp = driver.weeklyPattern;
    if (!wp) return 'available';
    const dowKey = ['works_sunday', 'works_monday', 'works_tuesday', 'works_wednesday', 'works_thursday', 'works_friday', 'works_saturday'][day.getDay()];
    return wp[dowKey] ? 'available' : 'off';
  };

  const getTimeRange = (driver, day) => {
    const wp = driver.weeklyPattern;
    if (!wp) return '';
    const dowFrom = ['sunday_from', 'monday_from', 'tuesday_from', 'wednesday_from', 'thursday_from', 'friday_from', 'saturday_from'][day.getDay()];
    const dowUntil = ['sunday_until', 'monday_until', 'tuesday_until', 'wednesday_until', 'thursday_until', 'friday_until', 'saturday_until'][day.getDay()];
    const from = wp[dowFrom];
    const until = wp[dowUntil];
    if (!from || !until) return '';
    return `${formatTime(from)}-${formatTime(until)}`;
  };

  const statusColors = {
    available: { bg: '#E1F5EE', color: '#0F6E56', label: 'On' },
    on_call:   { bg: '#FAEEDA', color: '#854F0B', label: 'OC' },
    off:       { bg: '#f5f5f3', color: '#ccc', label: 'Off' },
  };

  if (loading) return <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>;

  // Count available per day
  const dayCounts = days.map(day => data.filter(d => getStatus(d, day) !== 'off').length);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>
          {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setWeekStart(w => subWeeks(w, 1))} style={{ fontSize: 12, padding: '4px 10px' }}>← Prev</button>
          <button onClick={() => setWeekStart(startOfWeek(toET(new Date()), { weekStartsOn: 1 }))} style={{ fontSize: 12, padding: '4px 10px' }}>This week</button>
          <button onClick={() => setWeekStart(w => addWeeks(w, 1))} style={{ fontSize: 12, padding: '4px 10px' }}>Next →</button>
        </div>
      </div>

      <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e0e0db' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#999', fontWeight: 500, position: 'sticky', left: 0, background: 'white', minWidth: 120 }}>Driver</th>
              {days.map(day => (
                <th key={day.toISOString()} style={{
                  padding: '6px 4px', textAlign: 'center', fontSize: 10, fontWeight: 500,
                  color: isToday(day) ? '#185FA5' : '#999',
                  background: isToday(day) ? '#E6F1FB' : 'white',
                  minWidth: 65,
                }}>
                  <div>{format(day, 'EEE')}</div>
                  <div style={{ fontSize: 13, fontWeight: isToday(day) ? 600 : 400 }}>{format(day, 'd')}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.sort((a, b) => a.name.localeCompare(b.name)).map(driver => (
              <tr key={driver.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 500, position: 'sticky', left: 0, background: 'white' }}>
                  {driver.name}
                </td>
                {days.map(day => {
                  const s = getStatus(driver, day);
                  const sc = statusColors[s];
                  const timeRange = s === 'available' ? getTimeRange(driver, day) : '';
                  return (
                    <td key={day.toISOString()} style={{
                      padding: '4px 3px', textAlign: 'center',
                      background: isToday(day) ? '#E6F1FB22' : 'white',
                    }}>
                      <div style={{
                        background: sc.bg, color: sc.color, borderRadius: 4,
                        padding: '3px 2px', fontSize: 10, fontWeight: 500,
                      }}>
                        {sc.label}
                      </div>
                      {timeRange && <div style={{ fontSize: 8, color: '#999', marginTop: 1 }}>{timeRange}</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #e0e0db', background: '#f5f5f3' }}>
              <td style={{ padding: '6px 10px', fontSize: 11, fontWeight: 500, position: 'sticky', left: 0, background: '#f5f5f3' }}>Available</td>
              {dayCounts.map((count, i) => (
                <td key={i} style={{ textAlign: 'center', padding: '6px 3px' }}>
                  <span style={{
                    fontSize: 13, fontWeight: 600,
                    color: count < 3 ? '#A32D2D' : count < 5 ? '#854F0B' : '#0F6E56',
                  }}>{count}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: '#999' }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#E1F5EE', marginRight: 3 }}></span>Available</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#FAEEDA', marginRight: 3 }}></span>On call</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#f5f5f3', marginRight: 3 }}></span>Off</span>
        <span style={{ color: '#A32D2D' }}>Red count = low coverage</span>
      </div>
    </div>
  );
}

function AddRequestModal({ drivers, onClose, onSaved }) {
  const [form, setForm] = useState({
    driverId: '',
    requestType: 'time_off',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    message: '',
    status: 'approved',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.driverId || !form.startDate) { alert('Select a driver and start date'); return; }
    setSaving(true);
    try {
      await api.post(`/drivers/${form.driverId}/portal-request`, {
        requestType: form.requestType,
        startDate: form.startDate,
        endDate: form.endDate || form.startDate,
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        message: form.message || null,
      });
      if (form.status === 'approved') {
        const res = await api.get('/drivers/requests');
        const latest = (res.data.requests || [])
          .filter(r => r.driver_id === form.driverId && r.status === 'pending')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        if (latest) await api.put(`/drivers/requests/${latest.id}`, { status: 'approved' });
      }
      onSaved();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
      <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 400, margin: '0 16px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>Add time-off request</h2>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Driver</label>
          <select value={form.driverId} onChange={e => setForm(f => ({...f, driverId: e.target.value}))} style={{ width: '100%' }}>
            <option value="">Select driver...</option>
            {drivers.sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')).map(d => (
              <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Type</label>
          <select value={form.requestType} onChange={e => setForm(f => ({...f, requestType: e.target.value}))} style={{ width: '100%' }}>
            <option value="time_off">Time off / Vacation</option>
            <option value="call_in">Call in</option>
            <option value="availability_change">Availability change</option>
            <option value="note_to_admin">Note</option>
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div><label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Start date</label>
            <input type="date" value={form.startDate} onChange={e => setForm(f => ({...f, startDate: e.target.value}))} style={{ width: '100%' }} /></div>
          <div><label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>End date</label>
            <input type="date" value={form.endDate} onChange={e => setForm(f => ({...f, endDate: e.target.value}))} style={{ width: '100%' }} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div><label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Start time (optional)</label>
            <input type="time" value={form.startTime} onChange={e => setForm(f => ({...f, startTime: e.target.value}))} style={{ width: '100%' }} /></div>
          <div><label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>End time (optional)</label>
            <input type="time" value={form.endTime} onChange={e => setForm(f => ({...f, endTime: e.target.value}))} style={{ width: '100%' }} /></div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Status</label>
          <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} style={{ width: '100%' }}>
            <option value="approved">Pre-approved</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Notes</label>
          <input value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} placeholder="e.g. Vacation, doctor appt..." style={{ width: '100%' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontSize: 13, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '6px 16px' }}>
            {saving ? 'Saving...' : 'Add request'}
          </button>
        </div>
      </div>
    </div>
  );
}